import type SubjectAddress from '@Domain/Subjects/SubjectAddress';
import type { DocumentImportFailedAction } from '@Application/Beneficiaries/ImportedDocuments/Actions/documentImportFailed';
import ImportDocument from '@Application/Beneficiaries/ImportedDocuments/Commands/ImportDocument';
import importDocument from '@Application/Beneficiaries/ImportedDocuments/importDocument';
import FetchWorkflowRun from '@Application/WorkflowRuns/WorkflowRun/Queries/FetchWorkflowRun';
import fetchWorkflowRun from '@Application/WorkflowRuns/WorkflowRun/fetchWorkflowRun';
import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import { fieldMarginStyles } from '@Guidelines/Theme/commonStyles';
import { theme } from '@Guidelines/Theme/theme';
import importDocumentSidebarSignal, {
	closeImportDocumentSidebar,
} from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Signals/importDocumentSidebarSignal';
import ImportDocumentFormData from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Sidebars/ImportDocumentSidebar/FormData/ImportDocumentFormData';
import ignoreHandledError from '@UI/Helpers/ignoreHandledError';
import useStrictCurrentFacility from '@UI/Providers/CurrentFacilityProvider/useStrictCurrentFacility';
import { css } from '@emotion/react';
import * as React from 'react';
import { updateValidationFromExternalSource } from '@verclam/form-validator';
import { useSignalValue } from '@verclam/pulsar';
import { useDispatch } from '@verclam/synapse';
import { Button, Form, InputText, Sidebar, TextArea } from '@verclam/ui-kit';

const hiddenInputStyle = css`
	position: absolute;
	width: 1px;
	height: 1px;
	padding: 0;
	margin: -1px;
	overflow: hidden;
	clip: rect(0, 0, 0, 0);
	white-space: nowrap;
	border: 0;
`;

const fileRowStyle = css`
	display: flex;
	align-items: center;
	gap: 0.75rem;
	flex-wrap: wrap;
`;

const fileHintStyle = css`
	margin: 0.5rem 0 0;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.small};
	color: ${theme.colors.inkMuted};
`;

const fileErrorStyle = css`
	margin: 0.375rem 0 0;
`;

type Props = {
	readonly subject: SubjectAddress;
};

/** Import panel: file picker (native input, hidden), name, description. Imports into the dossier, or into a preparation when the signal carries one. Closes on success. */
const ImportDocumentSidebar: React.FC<Props> = ({ subject }) => {
	const dispatch = useDispatch();
	const { currentFacility } = useStrictCurrentFacility();
	const sidebarState = useSignalValue(importDocumentSidebarSignal);
	const isVisible = sidebarState !== null;
	const preparationId = sidebarState?.preparationId ?? null;

	const [formData, setFormData] = React.useState(() => ImportDocumentFormData.createEmpty());
	const [isSubmitting, setIsSubmitting] = React.useState(false);
	const [serverFileError, setServerFileError] = React.useState<string | null>(null);

	const formId = React.useId();
	const fileInputId = React.useId();
	const fileInputRef = React.useRef<HTMLInputElement>(null);

	// Each opening starts from a blank form.
	React.useEffect(() => {
		if (isVisible) {
			setFormData(ImportDocumentFormData.createEmpty());
			setServerFileError(null);
		}
	}, [isVisible]);

	const handleNameChange = React.useCallback((value: string) => {
		setFormData((current) => current.setName(value));
	}, []);

	const handleDescriptionChange = React.useCallback((value: string) => {
		setFormData((current) => current.setDescription(value));
	}, []);

	const handleBrowseClick = React.useCallback(() => {
		fileInputRef.current?.click();
	}, []);

	const handleFileInputChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0];

		// Cleared so the same file can be chosen again after a rejection.
		event.target.value = '';

		if (file === undefined) {
			return;
		}

		setFormData((current) => current.setFile(file));
		setServerFileError(null);
	}, []);

	const handleSubmit = React.useCallback(() => {
		if (!formData.isFormDataValid()) {
			setFormData(formData.showErrors());
			return;
		}

		if (formData.file === null) {
			return;
		}

		const description = formData.description.trim() === '' ? null : formData.description.trim();

		setIsSubmitting(true);

		dispatch(
			importDocument(
				new ImportDocument(
					currentFacility.id,
					subject,
					formData.file,
					formData.name.trim(),
					description,
					preparationId,
				),
			),
		)
			.then(() => {
				closeImportDocumentSidebar();

				// The new source shows in the preparation without waiting for the event stream.
				if (preparationId !== null) {
					dispatch(
						fetchWorkflowRun(new FetchWorkflowRun(currentFacility.id, subject, preparationId)),
					).catch(ignoreHandledError);
				}
			})
			.catch((action: DocumentImportFailedAction) => {
				updateValidationFromExternalSource({
					action,
					eventNames: 'DOCUMENT_IMPORT_FAILED',
					formDataUpdater: setFormData,
				});
				// A 413/422 carries no `fieldErrors`: its message belongs under the file picker.
				setServerFileError(
					action.payload !== undefined && action.payload.fieldErrors.length === 0
						? action.payload.message
						: null,
				);
			})
			.finally(() => setIsSubmitting(false));
	}, [formData, currentFacility.id, subject, preparationId, dispatch]);

	const fileErrorMessage = formData.getValidationMessage('file') ?? serverFileError ?? undefined;

	return (
		<Sidebar
			visible={isVisible}
			onHide={closeImportDocumentSidebar}
			position="right"
			header="Importer un document"
			footer={
				<React.Fragment>
					<Button
						type="button"
						severity="secondary"
						outlined
						label="Annuler"
						onClick={closeImportDocumentSidebar}
					/>
					<Button type="submit" form={formId} label="Importer" loading={isSubmitting} />
				</React.Fragment>
			}
			blockScroll
		>
			<Form id={formId} onSubmit={handleSubmit} aria-label="Importer un document">
				<div css={fieldMarginStyles}>
					<div css={fileRowStyle}>
						<Button type="button" label="Choisir un fichier" onClick={handleBrowseClick} />
						<ConditionalLoader data={formData.file}>
							{(file) => <span>{file.name}</span>}
						</ConditionalLoader>
					</div>
					<label css={hiddenInputStyle} htmlFor={fileInputId}>
						Choisir un fichier Word ou PDF
					</label>
					<input
						ref={fileInputRef}
						id={fileInputId}
						css={hiddenInputStyle}
						type="file"
						accept=".docx,.pdf"
						onChange={handleFileInputChange}
					/>
					<p css={fileHintStyle}>
						Word (.docx) ou PDF · 20 Mo maximum · les PDF scannés ne sont pas pris en charge.
					</p>
					<ConditionalLoader data={fileErrorMessage}>
						<small css={fileErrorStyle}>{fileErrorMessage}</small>
					</ConditionalLoader>
				</div>

				<InputText
					name="name"
					label="Nom"
					required
					value={formData.name}
					onChange={handleNameChange}
					errorMessage={formData.getValidationMessage('name')}
					isFormFieldValid={formData.isValid('name')}
				/>
				<TextArea
					name="description"
					label="Description"
					value={formData.description}
					onChange={handleDescriptionChange}
					errorMessage={formData.getValidationMessage('description')}
					isFormFieldValid={formData.isValid('description')}
				/>
			</Form>
		</Sidebar>
	);
};

export default ImportDocumentSidebar;
